import { inject } from '@angular/core';
import type { Route } from '@angular/router';

import { LanguageService } from '@rpgc-ai/services/translate/language.service';

import { EChatbotCreateStatus } from '@rpgc-ai/shared-interfaces/interfaces/chatbot-create.interface';

import { authGuard, tosGuard } from './core/guards/auth.guard';
import { DevelopEnvironmentGuard } from './core/guards/develop-environment.guard';
import { LanguageGuard } from './core/guards/language.guard';
import { modalRoutes } from './modal.routes';

export const appRoutes: Route[] = [
  // Маршрут для отображения SVG по адресу /null
  {
    path: 'null',
    loadComponent: () => import('@rpgc-ai/pages/svg-page/svg-page.component').then((m) => m.SvgPageComponent),
  },
  ...modalRoutes,
  {
    path: ':lang',
    canActivate: [LanguageGuard],
    children: [
      /**
       * Chat
       */
      {
        path: 'chat/:id',
        loadComponent: () => import('@rpgc-ai/layout/chat/chat.component').then((m) => m.ChatComponent),
        canActivate: [tosGuard],
      },
      {
        path: '',
        loadComponent: () => import('@rpgc-ai/layout/main/main.component').then((m) => m.MainComponent),
        children: [
          /**
           * Характеры
           */
          {
            path: '',
            loadComponent: () => import('@rpgc-ai/pages/home/home.component').then((m) => m.HomeComponent),
          },

          /**
           * Мой профиль
           */
          {
            path: 'my-profile',
            data: { title: 'My profile', backLink: true },
            loadComponent: () => import('@rpgc-ai/pages/my-profile/my-profile.component').then((m) => m.MyProfileComponent),
            canActivate: [authGuard],
          },

          /**
           * Создание чатбота
           */
          {
            path: 'create-chatbot',
            data: { chatbotStatus: EChatbotCreateStatus.CREATE },
            loadComponent: () => import('@rpgc-ai/pages/chatbot/chatbot-create.component').then((m) => m.ChatbotCreateComponent),
            canActivate: [authGuard, tosGuard],
          },
          /**
           * Просмотр и редактирование чатбота
           */
          {
            path: 'view-chatbot/:botId',
            loadComponent: () => import('@rpgc-ai/pages/chatbot/chatbot-create.component').then((m) => m.ChatbotCreateComponent),
            canActivate: [authGuard, tosGuard],
          },
          {
            path: 'view-chatbot/:creatorSlug/:botId',
            loadComponent: () => import('@rpgc-ai/pages/chatbot/chatbot-create.component').then((m) => m.ChatbotCreateComponent),
            canActivate: [authGuard, tosGuard],
          },
          /**
           * Мои чатботы
           */
          {
            path: 'my-chatbots',
            loadComponent: () => import('@rpgc-ai/pages/my-chatbots/my-chatbots.component').then((m) => m.MyChatbotsComponent),
            canActivate: [authGuard, tosGuard],
          },

          /**
           * Избранные чатботы
           */
          {
            path: 'favorites',
            loadComponent: () => import('@rpgc-ai/pages/favorites/favorites.component').then((m) => m.FavoritesComponent),
            canActivate: [authGuard, tosGuard],
          },

          /**
           * История чатов
           */
          {
            path: 'chat-history',
            data: { title: 'Chat History', backLink: true },
            loadComponent: () => import('@rpgc-ai/pages/chat-history/chat-history.component').then((m) => m.ChatHistoryComponent),
            canActivate: [authGuard, tosGuard],
          },

          /**
           * Заблокированные создатели чатботов
           */
          {
            path: 'blocked-creators',
            data: { title: 'Blocked Creators', backLink: true },
            loadComponent: () =>
              import('@rpgc-ai/pages/blocked-creators/blocked-creators.component').then((m) => m.BlockedCreatorsComponent),
            canActivate: [authGuard, tosGuard],
          },

          /**
           * Мои персонажи
           */
          {
            path: 'my-personas',
            data: { title: 'My Personas', backLink: true },
            loadComponent: () => import('@rpgc-ai/pages/my-personas/my-personas.component').then((m) => m.MyPersonasComponent),
            canActivate: [authGuard, tosGuard],
          },

          /**
           * Создание персонажа
           */
          {
            path: 'create-persona',
            data: { title: 'Create Persona', backLink: true },
            loadComponent: () =>
              import('@rpgc-ai/pages/create-persona-page/create-persona-page.component').then((m) => m.CreatePersonaPageComponent),
            canActivate: [authGuard, tosGuard],
          },

          {
            path: 'edit-persona/:id',
            data: { title: 'Edit Persona', backLink: true },
            loadComponent: () =>
              import('@rpgc-ai/pages/create-persona-page/create-persona-page.component').then((m) => m.CreatePersonaPageComponent),
            canActivate: [authGuard, tosGuard],
          },

          /**
           * Subscribe
           */
          {
            path: 'subscribe',
            data: { title: 'Subscribe', backLink: true },
            loadComponent: () => import('@rpgc-ai/pages/subscribe-page/subscribe.component').then((m) => m.SubscribePageComponent),
          },

          {
            path: 'help',
            data: { title: 'Help' },
            loadChildren: () => import('@rpgc-ai/pages/help/help.routes').then((r) => r.helpRoutes),
            // loadComponent: () => import('@rpgc-ai/pages/help/help.component').then((m) => m.HelpPageComponent),
          },

          /**
           * Public profile
           */
          {
            path: 'profile/:id',
            data: { title: 'User profile', backLink: true },
            loadComponent: () => import('@rpgc-ai/pages/public-profile/public-profile.component').then((m) => m.PublicProfileComponent),
          },

          /**
           * Необходима авторизация
           */
          {
            path: 'need-auth',
            loadComponent: () =>
              import('@rpgc-ai/lazy-blocks/do-you-need-auth/do-you-need-auth.component').then((m) => m.DoYouNeedAuthComponent),
          },
          /**
           * terms & conditions
           */
          {
            path: 'terms-and-conditions',
            data: { title: 'Terms & Conditions', backLink: true },
            loadComponent: () =>
              import('@rpgc-ai/pages/terms-and-conditions/terms-and-conditions.component').then((m) => m.TermsAndConditionsComponent),
          },
          /**
           * privacy-policy
           */
          {
            path: 'privacy-policy',
            data: { title: 'Privacy Policy', backLink: true },
            loadComponent: () => import('@rpgc-ai/pages/privacy-policy/privacy-policy.component').then((m) => m.PrivacyPolicyComponent),
          },
          /**
           *  refund-policy
           */
          {
            path: 'refund-policy',
            data: { title: 'Privacy Policy', backLink: true },
            loadComponent: () => import('@rpgc-ai/pages/refund-policy/refund-policy.component').then((m) => m.RefundPolicyComponent),
          },
          /**
           * Languages Admin
           */
          {
            path: 'languages-admin',
            data: { title: 'Languages Admin', backLink: true },
            loadComponent: () => import('@rpgc-ai/pages/languages-admin/languages-admin.component').then((m) => m.LanguagesAdminComponent),
            canActivate: [DevelopEnvironmentGuard],
          },

          // Community
          {
            path: 'blocked-content-policy',
            loadComponent: () =>
              import('@rpgc-ai/pages/blocked-content-policy/blocked-content-policy.component').then((m) => m.BlockedContentPolicyComponent),
          },
          {
            path: 'community-guidelines',
            loadComponent: () =>
              import('@rpgc-ai/pages/community-guidelines/community-guidelines.component').then((m) => m.CommunityGuidelinesComponent),
          },
          {
            path: 'content-removal-policy',
            loadComponent: () =>
              import('@rpgc-ai/pages/content-removal-policy/content-removal-policy.component').then((m) => m.ContentRemovalPolicyComponent),
          },
          {
            path: 'underage-policy',
            loadComponent: () => import('@rpgc-ai/pages/underage-policy/underage-policy.component').then((m) => m.UnderagePolicyComponent),
          },

          {
            path: 'payment/:status/:id',
            loadComponent: () => import('@rpgc-ai/pages/payment/payment.component').then((m) => m.PaymentComponent),
          },

          {
            path: '**',
            redirectTo: '',
          },
        ],
      },

      {
        path: '**',
        redirectTo: '',
      },
    ],
  },
  // Wildcard маршрут для обработки всех несуществующих URL
  {
    path: '',
    pathMatch: 'full',
    redirectTo: () => {
      const languageService = inject(LanguageService);
      return languageService.initLanguage();
    },
  },
  {
    path: '**',
    redirectTo: () => {
      const languageService = inject(LanguageService);
      return languageService.initLanguage();
    },
  },
];
